import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Button, Card, CardContent, Checkbox, CircularProgress, FormControlLabel,
  Stack, TextField, Typography
} from '@mui/material';
import {
  acknowledgeReferralUrgentFinding,
  createExternalReferral,
  listExternalReferrals,
  resendExternalReferral,
  revokeExternalReferral,
  searchReferralStudies
} from '../../services/externalReportingReferrals';
import { downloadReportPDF, printReportPDF } from '../../services/reports';

const STATUS_LABELS = {
  pending_acceptance: 'Pending Acceptance',
  accepted: 'Accepted',
  in_progress: 'In Progress',
  completed: 'Completed',
  declined: 'Declined',
  cancelled: 'Cancelled',
  expired: 'Expired'
};

function toLocalDateTimeValue(date) {
  const local = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 16);
}

export default function ExternalReportingDashboard() {
  const [referrals, setReferrals] = useState([]);
  const [query, setQuery] = useState('');
  const [studies, setStudies] = useState([]);
  const [selectedStudy, setSelectedStudy] = useState(null);
  const [email, setEmail] = useState('');
  const [clinicianName, setClinicianName] = useState('');
  const [deadline, setDeadline] = useState(() => toLocalDateTimeValue(new Date(Date.now() + 7 * 86400000)));
  const [sharedNotes, setSharedNotes] = useState('');
  const [sharedDocuments, setSharedDocuments] = useState([]);
  const [amendmentOf, setAmendmentOf] = useState('');
  const [amendmentReason, setAmendmentReason] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [noticeSeverity, setNoticeSeverity] = useState('success');

  const loadReferrals = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await listExternalReferrals();
      setReferrals(data.referrals || []);
    } catch (loadError) {
      setError(loadError.response?.data?.error || 'Unable to load external referrals.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadReferrals();
  }, [loadReferrals]);

  useEffect(() => {
    if (query.trim().length < 2) {
      setStudies([]);
      return undefined;
    }
    let active = true;
    const timer = window.setTimeout(async () => {
      try {
        const data = await searchReferralStudies(query.trim());
        if (active) setStudies(data.studies || []);
      } catch (searchError) {
        if (active) setError(searchError.response?.data?.error || 'Unable to search examinations.');
      }
    }, 300);
    return () => {
      active = false;
      window.clearTimeout(timer);
    };
  }, [query]);

  const canCreate = useMemo(() =>
    Boolean(selectedStudy && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim()) &&
      new Date(deadline) > new Date() && (!amendmentOf || amendmentReason.trim())),
  [selectedStudy, email, deadline, amendmentOf, amendmentReason]);

  const resetForm = () => {
    setQuery('');
    setStudies([]);
    setSelectedStudy(null);
    setEmail('');
    setClinicianName('');
    setSharedNotes('');
    setSharedDocuments([]);
    setAmendmentOf('');
    setAmendmentReason('');
  };

  const handleCreate = async event => {
    event.preventDefault();
    setSaving(true);
    setError('');
    setNotice('');
    try {
      const result = await createExternalReferral({
        studyId: selectedStudy._id,
        invitedEmail: email.trim(),
        clinicianName: clinicianName.trim(),
        expiresAt: new Date(deadline).toISOString(),
        sharedNotes,
        sharedDocumentReportIds: sharedDocuments,
        amendmentOf: amendmentOf || undefined,
        amendmentReason: amendmentReason.trim()
      });
      setNoticeSeverity(result.delivery === 'sent' ? 'success' : 'warning');
      setNotice(result.delivery === 'sent'
        ? 'Referral created and invitation email sent.'
        : result.deliveryError || 'Referral created, but the invitation email was not delivered.');
      resetForm();
      await loadReferrals();
    } catch (createError) {
      setError(createError.response?.data?.error || 'Unable to create the referral.');
    } finally {
      setSaving(false);
    }
  };

  const handleResend = async referral => {
    setError('');
    setNotice('');
    try {
      await resendExternalReferral(referral.id);
      setNotice(`A new invitation was sent to ${referral.invitedEmail}. The prior link was invalidated.`);
      await loadReferrals();
    } catch (resendError) {
      setError(resendError.response?.data?.error || 'Unable to resend the invitation.');
    }
  };

  const handleRevoke = async id => {
    setError('');
    try {
      await revokeExternalReferral(id);
      await loadReferrals();
    } catch (revokeError) {
      setError(revokeError.response?.data?.error || 'Unable to cancel the referral.');
    }
  };

  const handleAcknowledge = async id => {
    setError('');
    try {
      await acknowledgeReferralUrgentFinding(id);
      await loadReferrals();
    } catch (ackError) {
      setError(ackError.response?.data?.error || 'Unable to acknowledge the urgent finding.');
    }
  };

  const startAmendment = referral => {
    setSelectedStudy({ _id: referral.studyId, ...referral.studySnapshot });
    setAmendmentOf(referral.finalReport?._id || referral.finalReport || '');
    setAmendmentReason('');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <Box sx={{ maxWidth: 1300, mx: 'auto', py: 2 }}>
      <Typography variant="h4" gutterBottom>External reporting referrals</Typography>
      <Typography color="text.secondary" sx={{ mb: 2 }}>
        Invite external clinicians to report on one examination. Invitations are passwordless, free, and limited to the selected case.
      </Typography>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {notice && <Alert severity={noticeSeverity} sx={{ mb: 2 }}>{notice}</Alert>}

      <Card sx={{ mb: 3 }}>
        <CardContent component="form" onSubmit={handleCreate}>
          <Typography variant="h6" gutterBottom>{amendmentOf ? 'Invite an amendment' : 'Create referral'}</Typography>
          <Stack spacing={2}>
            {!selectedStudy ? (
              <>
                <TextField label="Find examination by patient, MRN, accession, description or Study UID"
                  value={query} onChange={event => setQuery(event.target.value)} />
                {studies.map(study => (
                  <Button key={study._id} variant="outlined" sx={{ justifyContent: 'flex-start', textAlign: 'left' }}
                    onClick={() => { setSelectedStudy(study); setQuery(''); setStudies([]); }}>
                    {study.patientName} · {study.patientId} · {study.modality} · {study.studyDescription || 'Imaging examination'} · {study.accessionNumber || 'No accession'}
                  </Button>
                ))}
              </>
            ) : (
              <Alert severity="info" action={<Button onClick={() => { setSelectedStudy(null); setAmendmentOf(''); setAmendmentReason(''); }}>Change</Button>}>
                {selectedStudy.patientName} · {selectedStudy.patientId} · {selectedStudy.modality} · {selectedStudy.studyDescription || 'Imaging examination'}
              </Alert>
            )}
            <TextField required type="email" label="External clinician email" value={email} onChange={event => setEmail(event.target.value)} />
            <TextField label="Clinician name (optional)" value={clinicianName} onChange={event => setClinicianName(event.target.value)} />
            <TextField required type="datetime-local" label="Invitation deadline" value={deadline}
              onChange={event => setDeadline(event.target.value)} InputLabelProps={{ shrink: true }} />
            <TextField label="Clinical notes to share (optional)" multiline minRows={2} value={sharedNotes}
              onChange={event => setSharedNotes(event.target.value)} />
            {amendmentOf && (
              <TextField required label="Amendment reason" multiline minRows={2} value={amendmentReason}
                onChange={event => setAmendmentReason(event.target.value)} />
            )}
            {selectedStudy?.availableDocuments?.length > 0 && (
              <Box>
                <Typography variant="subtitle2">Supporting reports to share</Typography>
                {selectedStudy.availableDocuments.map(document => (
                  <FormControlLabel key={document.id} control={
                    <Checkbox checked={sharedDocuments.includes(document.id)} onChange={event => {
                      setSharedDocuments(previous => event.target.checked
                        ? [...previous, document.id]
                        : previous.filter(id => id !== document.id));
                    }} />
                  } label={document.label} />
                ))}
              </Box>
            )}
            <Button type="submit" variant="contained" disabled={!canCreate || saving}>
              {saving ? <CircularProgress size={20} /> : amendmentOf ? 'Send amendment invitation' : 'Send referral invitation'}
            </Button>
          </Stack>
        </CardContent>
      </Card>

      <Typography variant="h6" sx={{ mb: 1 }}>Referral activity</Typography>
      {loading ? <CircularProgress /> : referrals.length === 0 ? (
        <Alert severity="info">No external referrals yet.</Alert>
      ) : (
        <Stack spacing={1.5}>
          {referrals.map(referral => (
            <Card key={referral.id}><CardContent>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} justifyContent="space-between">
                <Box>
                  <Typography variant="subtitle1">{referral.invitedClinicianName || referral.invitedEmail}</Typography>
                  <Typography variant="body2">{referral.invitedEmail} · {STATUS_LABELS[referral.status] || referral.status}</Typography>
                  {referral.studySnapshot && (
                    <Typography variant="body2" color="text.secondary">
                      {referral.studySnapshot.patientName} · {referral.studySnapshot.patientId} · {referral.studySnapshot.modality} · {referral.studySnapshot.studyDescription || 'Imaging examination'}
                    </Typography>
                  )}
                  <Typography variant="caption" color="text.secondary">
                    Expires {new Date(referral.expiresAt).toLocaleString()}
                    {referral.amendmentOf ? ` · Amendment v${referral.amendmentVersion}` : ''}
                    {referral.completedAt ? ` · Completed ${new Date(referral.completedAt).toLocaleString()}` : ''}
                  </Typography>
                  {referral.urgentFinding?.raisedAt && (
                    <Alert severity={referral.urgentFinding.acknowledgedAt ? 'success' : 'warning'} sx={{ mt: 1 }}>
                      {referral.urgentFinding.message && <><strong>Urgent finding:</strong> {referral.urgentFinding.message} · </>}
                      {referral.urgentFinding.acknowledgedAt
                        ? `acknowledged ${new Date(referral.urgentFinding.acknowledgedAt).toLocaleString()}`
                        : 'awaiting acknowledgment'}
                      {!referral.urgentFinding.acknowledgedAt && (
                        <Button size="small" onClick={() => handleAcknowledge(referral.id)}>Acknowledge</Button>
                      )}
                    </Alert>
                  )}
                  {referral.events?.length > 0 && (
                    <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                      Latest activity: {referral.events[referral.events.length - 1].type.replaceAll('_', ' ')} · {new Date(referral.events[referral.events.length - 1].at).toLocaleString()}
                    </Typography>
                  )}
                </Box>
                <Stack direction="row" spacing={1} alignItems="flex-start" flexWrap="wrap">
                  {['pending_acceptance', 'accepted', 'in_progress'].includes(referral.status) && (
                    <>
                      <Button size="small" onClick={() => handleResend(referral)}>Resend link</Button>
                      <Button size="small" color="error" onClick={() => handleRevoke(referral.id)}>Revoke</Button>
                    </>
                  )}
                  {referral.status === 'completed' && referral.finalReport && (
                    <>
                      <Button size="small" onClick={() => downloadReportPDF(referral.finalReport._id || referral.finalReport).catch(downloadError => {
                        setError(downloadError.response?.data?.error || 'Unable to download the finalized report.');
                      })}>Download report</Button>
                      <Button size="small" onClick={() => printReportPDF(referral.finalReport._id || referral.finalReport).catch(printError => {
                        setError(printError.response?.data?.error || 'Unable to print the finalized report.');
                      })}>Print</Button>
                      <Button size="small" onClick={() => startAmendment(referral)}>Invite amendment</Button>
                    </>
                  )}
                </Stack>
              </Stack>
            </CardContent></Card>
          ))}
        </Stack>
      )}
    </Box>
  );
}
